import {useCallback} from 'react'
import {utcToZonedTime} from '@wix/table-reservations-lib/timezone'
import {useBi, useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import {Reservation} from '@wix/ambassador-table-reservations-v1-reservation/types'

import {useReservationDetailsStorage} from '../../storage'
import {getReservationLocationById} from '../../../../utils/reservationLocation'
import {getReservationSummaryLocationText} from '../../../../utils/areas'
import {getLogger} from '../../../../utils/getLogger'
import {usePaymentDetails} from '../../../../utils/usePaymentDetails'
import {useExperiencePaymentDetails} from '../../../../utils/useExperiencePaymentDetails'
import {isPaymentTokenizationSupported} from '../../../../utils/paymentTokenization'

export const useHooks = () => {
  const {
    reservationLocations,
    reservation,
    changeReservation,
    regionalSettings,
    isManualApproval,
    reservationDataQueryParams,
    paymentMethods,
    experience,
  } = useReservationDetailsStorage()

  const bi = useBi()
  const logger = getLogger(bi)
  const {isPreview} = useEnvironment()
  const {t} = useTranslation()

  const currentReservationLocation = getReservationLocationById(
    (isManualApproval
      ? reservationDataQueryParams?.reservationLocationId
      : reservation?.details?.reservationLocationId) ?? '',
    reservationLocations,
  )

  const timeZone = currentReservationLocation?.location?.timeZone

  const handleChangeReservationClick = useCallback(() => {
    const reservationToChange = isManualApproval
      ? {
          details: {
            startDate: new Date(reservationDataQueryParams?.startDate!),
            partySize: Number(reservationDataQueryParams?.partySize),
            reservationLocationId: reservationDataQueryParams?.reservationLocationId,
            areaId: reservationDataQueryParams?.areaId,
          },
        }
      : reservation

    changeReservation(reservationToChange)
    logger.clickOnChangeReservationDetails({
      isPreview,
      reservation: reservationToChange as Reservation,
      locationId: currentReservationLocation?.location?.id,
      reservationLocationId: currentReservationLocation?.id,
    })
  }, [reservation, changeReservation])

  const startDate = utcToZonedTime(
    isManualApproval ? reservationDataQueryParams?.startDate! : reservation?.details?.startDate!,
    timeZone,
  )

  const partySize = Number(
    isManualApproval ? reservationDataQueryParams?.partySize : reservation?.details?.partySize,
  )
  const areaId = isManualApproval
    ? reservationDataQueryParams?.areaId
    : reservation?.details?.areaId
  const locationText = getReservationSummaryLocationText({
    reservationLocation: currentReservationLocation,
    areaId,
    t,
  })

  const reservationPaymentDetails = usePaymentDetails(currentReservationLocation, partySize)
  const experiencePaymentDetails = useExperiencePaymentDetails(experience, partySize)
  const {isPaymentRuleActive, isPerGuestPayment, pricePerUnitWithCurrency, totalPriceWithCurrency} =
    experience ? experiencePaymentDetails : reservationPaymentDetails

  const paymentTokenizationSupported = isPaymentTokenizationSupported(paymentMethods)

  return {
    isManualApproval,
    startDate,
    partySize,
    regionalSettings,
    locationName: locationText,
    isPaymentRuleActive,
    isPerGuestPayment,
    pricePerUnitWithCurrency,
    totalPriceWithCurrency,
    paymentTokenizationSupported,
    handleChangeReservationClick,
    experience,
  }
}
